import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Image from '../AppImage';
import MobileMenuToggle from './MobileMenuToggle';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAdminButton, setShowAdminButton] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  const location = useLocation();

  const navigationItems = [
  {
    label: 'หน้าหลัก',
    path: '/home-dashboard',
    icon: 'Home',
    tooltip: 'หน้าแรกของเว็บไซต์'
  },
  {
    label: 'โครงสร้างองค์กร',
    path: '/organization-structure',
    icon: 'Building2',
    tooltip: 'ข้อมูลโครงสร้างและบุคลากร'
  },
  {
    label: 'ข่าวประชาสัมพันธ์',
    path: '/news-and-announcements',
    icon: 'Newspaper',
    tooltip: 'ข่าวสารและประกาศต่างๆ',
    submenu: [
      { label: 'สื่อประชาสัมพันธ์', path: '/news-and-announcements/media' },
      { label: 'ข่าวสารประชาสัมพันธ์', path: '/news-and-announcements/pr' },
      { label: 'หนังสือราชการ', path: '/news-and-announcements/official-docs' },
      { label: 'จัดซื้อจัดจ้าง', path: '/news-and-announcements/procurement' }
    ]
  },
  {
    label: 'บริการประชาชน',
    path: '/public-services-portal',
    icon: 'Users',
    tooltip: 'บริการสำหรับประชาชน',
    submenu: [
      { label: 'คู่มือประชาชน', path: '/citizen-guides' }
    ]
  },
  {
    label: 'ติดตามคำร้อง',
    path: '/track',
    icon: 'Search',
    tooltip: 'ติดตามสถานะเรื่องร้องเรียน'
  }];


  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  // ฟังก์ชันสำหรับดับเบิลคลิกเพื่อแสดงปุ่ม Admin
  const handleHeaderDoubleClick = (e) => {
    e.preventDefault();
    setShowAdminButton(true);
    // ซ่อนปุ่ม Admin หลังจาก 10 วินาที
    setTimeout(() => {
      setShowAdminButton(false);
    }, 10000);
  };

  return (
    <header className="sticky top-0 z-100 bg-card/95 backdrop-blur border-b border-border shadow-government">
      <div className="max-w-7xl mx-auto">
        <div 
          className="flex items-center h-16 px-4 lg:px-6"
          onDoubleClick={handleHeaderDoubleClick}
        >
          {/* Logo Section */}
          <div className="flex items-center space-x-3">
            <Link
              to="/home-dashboard"
              className="flex items-center space-x-3 transition-government hover:opacity-80"
              onClick={handleMobileMenuClose}>

              <div className="w-12 h-12 flex items-center justify-center">
                <Image 
                  src="/assets/images/b822850b984eba9df2620697399b50075bab49cf64ce35210033003315257900-1757413105446.png"
                  alt="ตราสัญลักษณ์กรมการปกครอง"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-heading font-semibold text-primary">ที่ทำการปกครองอำเภอเทิง</h1>
                <p className="text-xs text-muted-foreground -mt-1">
                  District Office Portal
                </p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - centered */}
          <nav className="hidden md:flex items-center space-x-1 mx-auto">
            {navigationItems?.map((item) => (
              <div key={item?.path} className="relative group">
                <Link
                  to={item?.path}
                  className={`
                      relative flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium
                      transition-government hover:bg-[#F5F5F5]
                      ${isActivePath(item?.path) ?
                  'bg-primary text-primary-foreground hover:bg-primary/90' :
                  'text-foreground hover:text-[#D4A017]'}
                    `
                  }
                  title={item?.tooltip}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                  {item?.submenu && (
                    <span className="ml-1 text-muted-foreground">
                      <Icon
                        name="ChevronDown"
                        size={14}
                        className="transition-transform duration-200 ease-out opacity-80 group-hover:rotate-180"
                      />
                    </span>
                  )}
                  {/* animated underline */}
                  <span
                    className={`
                      pointer-events-none absolute left-4 right-4 bottom-1 h-0.5 bg-[#D4A017]
                      origin-left scale-x-0 transition-transform duration-200 ease-out
                      ${isActivePath(item?.path) ? 'scale-x-100' : 'group-hover:scale-x-100'}
                    `}
                  />
                </Link>

                {/* Submenu for Public Services */}
                {item?.submenu && (
                  <div className="absolute top-full left-0 mt-0 w-64 bg-card border border-border rounded-md shadow-government overflow-hidden hidden group-hover:block hover:block z-[60]
                                  transition-all duration-200 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0">
                    {item.submenu.map((sub) => (
                      <Link
                        key={sub.path + sub.label}
                        to={sub.path}
                        className="block px-4 py-3 text-sm text-foreground hover:bg-primary hover:text-primary-foreground transition-government"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {/* Admin Dashboard Link - แสดงเมื่อดับเบิลคลิก */}
            {showAdminButton && (
              <Link
                to="/admin"
                className={`
                    flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium
                    transition-government hover:bg-muted border-l border-border ml-2 pl-4
                    ${isActivePath('/admin') ?
                'bg-secondary text-secondary-foreground hover:bg-secondary/90' :
                'text-muted-foreground hover:text-secondary-foreground'}
                  `
                }
                title="เข้าสู่ระบบเจ้าหน้าที่">

                  <Icon name="Shield" size={18} />
                  <span>Admin</span>
                </Link>
            )}
            
            {/* จองคิวออนไลน์ ปุ่มเด่นสีทอง */}
            <Button variant="gold" size="lg" asChild className="ml-3 shadow-lg hover:shadow-xl">
              <Link to="/online-booking" title="จองคิวออนไลน์" className="flex items-center">
                <Icon name="Calendar" size={18} />
                <span className="ml-2">จองคิวออนไลน์</span>
              </Link>
            </Button>
            
          </nav>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden">
            <MobileMenuToggle
              isOpen={isMobileMenuOpen}
              onToggle={handleMobileMenuToggle} />

          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen &&
        <div className="md:hidden border-t border-border bg-card relative z-[60]">
            <nav className="px-4 py-2 space-y-1">
              {navigationItems?.map((item) =>
            <Link
              key={item?.path}
              to={item?.path}
              onClick={handleMobileMenuClose}
              className={`
                    flex items-center space-x-3 px-3 py-3 rounded-md text-sm font-medium
                    transition-government min-h-44 active:scale-[0.98]
                    ${isActivePath(item?.path) ?
              'bg-primary text-primary-foreground' :
              'text-foreground hover:bg-muted hover:text-primary'}
                  `
              }> 

                  <Icon name={item?.icon} size={20} />
                  <div className="transition-transform duration-150 group-active:translate-x-0.5">
                    <div>{item?.label}</div>
                    <div className="text-xs opacity-75 mt-1">{item?.tooltip}</div>
                  </div>
                </Link>
            )}
            
            {/* Mobile จองคิวออนไลน์ */}
            <Link
              to="/online-booking"
              onClick={handleMobileMenuClose}
              className={`
                flex items-center space-x-3 px-3 py-3 rounded-md text-sm font-medium
                transition-government min-h-44 border-t border-border mt-2 pt-4
                ${isActivePath('/online-booking') ?
              'bg-red-600 text-white' :
              'bg-red-500 text-white hover:bg-red-600'}
              `}> 
              <Icon name="Calendar" size={20} />
              <div>
                <div>จองคิวออนไลน์</div>
                <div className="text-xs opacity-75 mt-1">ระบบจองคิวออนไลน์</div>
              </div>
            </Link>
            
            {/* Mobile Admin Link - แสดงเมื่อดับเบิลคลิก */}
            {showAdminButton && (
              <Link
                to="/admin"
                onClick={handleMobileMenuClose}
                className={`
                      flex items-center space-x-3 px-3 py-3 rounded-md text-sm font-medium
                      transition-government min-h-44 border-t border-border mt-2 pt-4
                      ${isActivePath('/admin') ?
                'bg-secondary text-secondary-foreground' :
                'text-muted-foreground hover:bg-muted hover:text-secondary-foreground'}
                    `
                }> 

                    <Icon name="Shield" size={20} />
                    <div>
                      <div>Admin Dashboard</div>
                      <div className="text-xs opacity-75 mt-1">เข้าสู่ระบบเจ้าหน้าที่</div>
                    </div>
                  </Link>
            )}
            </nav>
          </div>
        }
      </div>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen &&
      <div
        className="fixed inset-0 bg-black/20 z-[50] md:hidden"
        onClick={handleMobileMenuClose} />

      }
    </header>);

};

export default Header;